// Tracks which value-graph nodes just appeared, so the graph-peek panel can
// pulse them in when a promote/generation lands. Diffs node ids across canvas
// cache updates (React Query invalidation → refetch → new node list).

import { useEffect, useRef, useState } from 'react';
import { useCanvasData } from '@/hooks/use-canvas-data';

// How long a freshly-arrived node stays highlighted. Matches the CSS
// animation (3 × 1.2s) so the class is removed as the last pulse finishes.
const PULSE_MS = 3600;

// Pure diff: ids in `nextIds` that weren't in `prev`. A null `prev` means
// "first observation" — the initial load of an existing graph must not pulse.
export function newNodeIds(prev: Set<string> | null, nextIds: string[]): string[] {
  if (!prev) return [];
  return nextIds.filter((id) => !prev.has(id));
}

export function usePulseNodes(projectId: string): Set<string> {
  const { data } = useCanvasData(projectId);
  // Keyed by project so a project switch starts tracking afresh — never diff
  // one project's graph against another's, regardless of whether the page
  // remounts on navigation.
  const prevRef = useRef<{ projectId: string; ids: Set<string> } | null>(null);
  const [pulsing, setPulsing] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!data) return; // not loaded yet — don't treat "no data" as an empty graph
    const ids = data.nodes.map((n) => n.id);
    const prev = prevRef.current?.projectId === projectId ? prevRef.current.ids : null;
    const fresh = newNodeIds(prev, ids);
    prevRef.current = { projectId, ids: new Set(ids) };
    if (fresh.length === 0) return;

    setPulsing((prev) => new Set([...prev, ...fresh]));
    // Each batch clears exactly its own ids, so overlapping generations don't
    // cut each other's pulse short. setState after unmount is a no-op in
    // React 18, so no cleanup bookkeeping is needed for this throwaway timer.
    setTimeout(() => {
      setPulsing((prev) => {
        const next = new Set(prev);
        for (const id of fresh) next.delete(id);
        return next;
      });
    }, PULSE_MS);
  }, [data, projectId]);

  return pulsing;
}
